
import { Vue, Component, Prop } from "vue-property-decorator";
import BaseCard from "@/components/base/BaseCard.vue";
import BaseTable from "@/components-UI/table/BaseTable.vue";
import ViewDateTime from "@/components/base/ViewDateTime.vue";
import TdStatus from "@/components/base/TableUI/TdStatus.vue";
import TdActions from "@/components/base/TableUI/TdActions.vue";
import { Affiliate } from "@/services/affiliates/Affiliate";
import { IEmployeeAffiliateMethod } from "@/api/types/payout";
import { formatPhone, translatePaymentsRequisite, translatePayoutsRequestStatus } from "@/utils/translate";
import { sortPayoutMethodFields } from "@/utils/payoutMethodFields";
import SvgStar from "@/assets/icons/star.svg";
import FormModal from "@/components/base/FormModal.vue";
import TitleActions from "@/components/affiliate/TitleActions.vue";
import { PAGINATION_RIGHT_PER_PAGE } from "@/configs/global";
import { CellsType, FormattedItem, RawConfig, prepareItems } from "@/utils/CellsFactory";
import BaseTooltip from "@/components-UI/BaseTooltip.vue";
import BaseButton from "@/components-UI/BaseButton.vue";
import SvgTrashWhite from "@/assets/icons/trash-white.svg";
import SvgCheck from "@/assets/icons/check.svg";
import BasePopup from "@/components-UI/BasePopup.vue";
import SvgVerticalDots from '@/assets/icons/vertical-dots.svg';
import {
    CONTRACT_APPROVE_REQUISITES_HINT,
    isContractApproveDataMissing as checkContractApproveDataMissing,
    isContractPayoutMethod,
    shouldHideManagerApproveButton,
    shouldHideManagerRejectButton,
} from '@/utils/payoutContractMethods';

@Component({
    components: {
        TitleActions,
        FormModal,
        BaseTable,
        ViewDateTime,
        TdStatus,
        TdActions,
        BaseCard,
        SvgStar,
        BaseTooltip,
        BaseButton,
        SvgTrashWhite,
        SvgCheck,
        BasePopup,
        SvgVerticalDots,
    },
})

export default class AffiliatePaymentInfo extends Vue {
    @Prop({ required: true }) affiliate!: Affiliate;

    isLoading: boolean = false;
    isShowModal: boolean = false;
    methodId: string | number = '';
    selectedStatusFilter: string[] = [];
    selectedCabinetFilter: boolean | null = null;
    itemsPerPage: number = PAGINATION_RIGHT_PER_PAGE;
    contractApproveRequisitesHint = CONTRACT_APPROVE_REQUISITES_HINT;
    isContractApproveDataMissing = checkContractApproveDataMissing;
    headers = [
        { text: 'Способ выплаты', value: 'method_name', width: 'auto' },
        { text: 'Реквизиты', value: 'requisites', width: 'auto' },
        { text: 'Дата создания', value: 'created_at', width: 'auto' },
        { text: 'Дата обновления', value: 'updated_at', width: 'auto' },
        { text: 'Статус', align: 'center', value: 'status', width: '150px' },
        { text: '', value: 'actions', sortable: false, width: '40px' },
    ];
    config: RawConfig<IEmployeeAffiliateMethod & { requisites: unknown, actions: unknown }> = {
        [CellsType.Date]: {
            created_at: {
                isLine: true,
            },
            updated_at: {
                isLine: true,
            },
        },
        [CellsType.Status]: {
            status: {
                translate: (s) => translatePayoutsRequestStatus(String(s)),
            },
        },
        [CellsType.Uniq]: {
            method_name: {},
            requisites: {},
            actions: {},
        },
    };
    popupTrigger: { [popupId: string | number]: boolean } = {};

    hideRejectButton(item: IEmployeeAffiliateMethod): boolean {
        return shouldHideManagerRejectButton(item.method_id, item.status ?? '');
    }

    approveButtonLabel(item: IEmployeeAffiliateMethod): string {
        return isContractPayoutMethod(item.method_id) ? 'Одобрить' : 'Принять';
    }

    hideApproveButton(item: IEmployeeAffiliateMethod): boolean {
        return shouldHideManagerApproveButton(item.method_id, item.status ?? '');
    }

    get isLoadingTable(): boolean {
        return this.affiliate.isLoadingData || this.isLoading;
    }

    get getItems(): FormattedItem<IEmployeeAffiliateMethod>[] {
        const raw = this.affiliate?.payment_info ?? [];
        const items = raw
            .sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))
            .map(e => ({ ...e, popupId: `popup-${Math.random().toString(36).slice(2)}` }));

        return prepareItems(items, this.config, this.headers.map(h => h.value));
    }

    handleClickActionButton(item: IEmployeeAffiliateMethod & { popupId: string }, event: boolean): void {
        this.popupTrigger = { ...this.popupTrigger, ...{ [item.popupId]: event } };
    }

    showModal(id: string | number): void {
        this.methodId = id;
        this.isShowModal = true;
    }

    hideModal(): void {
        this.methodId = '';
        this.isShowModal = false;
    }

    async makeDefault(): Promise<void> {
        this.isLoading = true;
        await this.affiliate.makeDefaultMethod(this.methodId);
        this.hideModal();
        this.isLoading = false;
    }

    async rejectOrApprove(type: 'approve' | 'reject', item: IEmployeeAffiliateMethod): Promise<void> {
        this.isLoading = true;

        if (type === 'approve') {
            await this.affiliate.approveMethod(item, this.selectedStatusFilter, this.selectedCabinetFilter);
        } else {
            await this.affiliate.rejectMethod(item.id, this.selectedStatusFilter, this.selectedCabinetFilter);
        }

        this.isLoading = false;
    }

    getRequisites(i: IEmployeeAffiliateMethod): { value: string, type: string }[] {
        const result: { value: string, type: string }[] = [];
        const partnerData = i?.partner_data;
        const managerData = i?.manager_data;
        const isPartnerData =
            partnerData != null &&
            typeof partnerData === 'object' &&
            !Array.isArray(partnerData);

        const addRequisites = (items: Record<string, unknown> | null | undefined): void => {
            if (items == null || typeof items !== 'object' || Array.isArray(items)) return;
            // Порядок ключей в объекте с бэка макету не соответствует, сортируем сами.
            for (const key of sortPayoutMethodFields(Object.keys(items), i.method_id)) {
                if (key !== 'legal_id') {
                    const type = translatePaymentsRequisite(key, i.method_id);
                    const value = key === 'phone' ? formatPhone(items[key] as string) : String(items[key]);
                    result.push({ value, type });
                }
            }
        };

        if (isPartnerData) {
            addRequisites(partnerData as Record<string, unknown>);
        } else {
            addRequisites(managerData as Record<string, unknown> | undefined);
        }

        return result;
    }
}

